import { Input } from 'components/Input';
import { RadioButtonGroup } from 'components/RadioButtonGroup/RadioButtonGroup';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrincipalInformationAndFeatures,
  PrincipalModalTypes,
} from 'utils/types';
import { EditPrincipalLogo } from './EditPrincipalLogo';
import { EditPrincipalAccessProps } from './EditPrincipalModal';

export const EditPrincipalInformation: React.FC<EditPrincipalAccessProps> = ({
  principalInformation,
  onChange,
  principalModalType,
}) => {
  const { t } = useTranslation('translation');

  const handlePropsChange = (
    key: keyof PrincipalInformationAndFeatures,
    value: string | number,
  ) => {
    if (!principalInformation) return;
    onChange({ ...principalInformation, [key]: value });
  };

  return (
    <>
      <p className="font-regular text-grey-700 mb-6 pt-1 text-left text-base leading-4">
        {t(
          `features:admin-center:principalAdministration:editPrincipalModal:principal-information:description`,
        )}
      </p>
      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-5">
        <div className="col-span-3">
          <InputField
            label={t(
              'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:pri-number',
            )}
            value={principalInformation?.id}
            disabled={principalModalType === PrincipalModalTypes.EDIT}
            onChange={(e) => {
              if (principalModalType === PrincipalModalTypes.CREATE) {
                handlePropsChange('id', e.target.value);
              }
            }}
          />
          <InputField
            label={t(
              'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:name',
            )}
            value={principalInformation?.name}
            onChange={(e) => handlePropsChange('name', e.target.value)}
          />
          <InputField
            label={t(
              'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:manufacturer-name',
            )}
            value={principalInformation?.manufacturerName}
            onChange={(e) =>
              handlePropsChange('manufacturerName', e.target.value)
            }
          />
          <InputField
            label={t(
              'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:slug',
            )}
            value={principalInformation?.slug}
            onChange={(e) => handlePropsChange('slug', e.target.value)}
          />
          <InputField
            label={t(
              'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:ad-group-id',
            )}
            value={principalInformation?.adGroupId}
            onChange={(e) => handlePropsChange('adGroupId', e.target.value)}
          />
          <div className="mb-4">
            <p className="mb-2">
              {t(
                'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:portfolio-level',
              )}
            </p>
            <RadioButtonGroup
              items={['1', '2'].map((level) => ({
                label: t(
                  'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:level',
                  { level },
                ),
                value: level,
              }))}
              groupName="portfolio-level"
              value={principalInformation?.portfolioLevel?.toString()}
              onChange={(value) =>
                handlePropsChange('portfolioLevel', Number(value))
              }
            />
          </div>
          <InputField
            label={t(
              'features:admin-center:principalAdministration:editPrincipalModal:principal-information:optionSections:team-id',
            )}
            value={principalInformation?.teamId}
            onChange={(e) => handlePropsChange('teamId', e.target.value)}
          />
        </div>
        <div className="col-span-2 mb-4">
          <EditPrincipalLogo
            logoUrl={principalInformation?.logoUrl}
            onUploadSuccess={(url: string) => {
              if (!principalInformation) return;
              onChange({ ...principalInformation, logoUrl: url });
            }}
          />
        </div>
      </div>
    </>
  );
};

interface InputFieldProps {
  label: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

const InputField = ({ label, value, onChange, disabled }: InputFieldProps) => {
  return (
    <div className="mb-4">
      <p className="mb-2">{label}</p>
      <Input
        type="text"
        onChange={onChange}
        initialValue={value}
        disabled={disabled}
      />
    </div>
  );
};
